// knowledgehubSlice.ts
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { KnowledgehubAgent } from "../../../app/api/knowledgehub/KnowledgehubAgent";
import { KnowledgehubFolderResponse } from "../../../app/models/responseModels/knowledgehubResponse";
import getAxiosError from "../../../app/api/axiosError";

interface KnowledgehubState {
  isLoading: boolean;
  knowledgehubList: KnowledgehubFolderResponse[];
  error: string | null;
  fdpRoles: { id: number; name: string }[];
  fdcRoles: { id: number; name: string }[];
  rolesLoading: boolean;
  organizationEmails: string[];
  emailsLoading: boolean;
}

const initialState: KnowledgehubState = {
  isLoading: false,
  knowledgehubList: [],
  error: null,
  fdpRoles: [],
  fdcRoles: [],
  rolesLoading: false,
  organizationEmails: [],
  emailsLoading: false,
};

export const getKnowledgehubList = createAsyncThunk(
  "knowledgehub/getList",
  async (_, { rejectWithValue }) => {
    try {
      const response = await KnowledgehubAgent.getKnowledgehubList();
      if (!response.isSuccess) {
        return rejectWithValue(response.message || "Failed to fetch knowledge hub");
      }
      return response.data || [];
    } catch (error) {
      const appError = getAxiosError(error);
      return rejectWithValue(appError.errorMessage);
    }
  }
);

export const getFdpRoles = createAsyncThunk(
  "knowledgehub/getFdpRoles",
  async (_, { rejectWithValue }) => {
    try {
      const response = await KnowledgehubAgent.getRolesByCategory("fdp");
      if (!response.isSuccess) {
        return rejectWithValue(response.message || "Failed to fetch FDP roles");
      }
      return response.data || [];
    } catch (error) {
      const appError = getAxiosError(error);
      return rejectWithValue(appError.errorMessage);
    }
  }
);

export const getFdcRoles = createAsyncThunk(
  "knowledgehub/getFdcRoles",
  async (_, { rejectWithValue }) => {
    try {
      const response = await KnowledgehubAgent.getRolesByCategory("fdc");
      if (!response.isSuccess) {
        return rejectWithValue(response.message || "Failed to fetch FDC roles");
      }
      return response.data || [];
    } catch (error) {
      const appError = getAxiosError(error);
      return rejectWithValue(appError.errorMessage);
    }
  }
);

export const getOrganizationEmails = createAsyncThunk<string[], void>(
  "knowledgehub/getOrganizationEmails",
  async (_, { rejectWithValue }) => {
    try {
      const response = await KnowledgehubAgent.getAllEmails();
      if (!response.isSuccess || !response.data) {
        return rejectWithValue(response.message || "Failed to fetch organization emails");
      }
      return response.data;
    } catch (error) {
      const appError = getAxiosError(error);
      return rejectWithValue(appError.errorMessage);
    }
  }
);

const knowledgehubSlice = createSlice({
  name: "knowledgehub",
  initialState,
  reducers: {
    clearKnowledgehub: (state) => {
      state.knowledgehubList = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getKnowledgehubList.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getKnowledgehubList.fulfilled, (state, action) => {
        state.isLoading = false;
        state.knowledgehubList = action.payload;
      })
      .addCase(getKnowledgehubList.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(getFdpRoles.pending, (state) => {
        state.rolesLoading = true;
      })
      .addCase(getFdpRoles.fulfilled, (state, action) => {
        state.rolesLoading = false;
        state.fdpRoles = action.payload;
      })
      .addCase(getFdpRoles.rejected, (state) => {
        state.rolesLoading = false;
      })
      .addCase(getFdcRoles.pending, (state) => {
        state.rolesLoading = true;
      })
      .addCase(getFdcRoles.fulfilled, (state, action) => {
        state.rolesLoading = false;
        state.fdcRoles = action.payload;
      })
      .addCase(getFdcRoles.rejected, (state) => {
        state.rolesLoading = false;
      })
      .addCase(getOrganizationEmails.pending, (state) => {
        state.emailsLoading = true;
      })
      .addCase(getOrganizationEmails.fulfilled, (state, action) => {
        state.emailsLoading = false;
        state.organizationEmails = action.payload;
      })
      .addCase(getOrganizationEmails.rejected, (state) => {
        state.emailsLoading = false;
      });
  },
});

export const { clearKnowledgehub } = knowledgehubSlice.actions;
export default knowledgehubSlice.reducer;